import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from 'helpers/api';
import cookie from 'react-cookies';

import { enable2FA } from 'api/user-slice';

const initialState = {
  company_id: null,
  user_id: null,
  checked: false,
  logged: false,
  admin: false,
  admin_role: null,
  is_primary: false,
  two_factor_auth_enabled: false,
  enforce_two_factor_auth: false,
};

export const authenticate = createAsyncThunk('authenticate', async () => {
  const res = await apiRequest('authentication', {}, 'get', true);
  return res.result;
});

export const verify2fa = createAsyncThunk('verify2fa', async ({ user_id, code }) => {
  const res = await apiRequest('authentication/2fa', { user_id, code }, 'post', true);
  return res.result;
});

export const login = createAsyncThunk('login', async ({ email, password }) => {
  const res = await apiRequest('authentication', { email, password }, 'post', true);
  return res;
});

export const loginWithGoogle = createAsyncThunk(
  'loginWithGoogle',
  async ({ credential, client_id }) => {
    const res = await apiRequest('authentication', { credential, client_id }, 'post', true);
    return res;
  },
);

export const loginAs = createAsyncThunk('loginAs', async userId => {
  const res = await apiRequest(`authentication/login_as/${userId}`, {}, 'post');
  return res.result;
});

export const verifySSO = createAsyncThunk('verifySSO', async code => {
  const res = await apiRequest('authentication/sso', { code }, 'post', true);
  return res.result;
});

const authenticationSlice = createSlice({
  name: 'authentication',
  initialState,
  reducers: {
    logout(state) {
      cookie.remove('token', { path: '/' });
      state.checked = true;
      state.logged = false;
      state.admin = false;
      state.is_primary = false;
      state.two_factor_auth_enabled = false;
      state.enforce_two_factor_auth = false;
    },
    stopInterpretation(state) {
      cookie.save('token', cookie.load('admin-token'), { path: '/' });
      cookie.remove('admin-token', { path: '/' });
      state.checked = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(authenticate.fulfilled, (state, { payload }) => {
        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.admin_role = payload.admin_role;
        state.is_primary = payload.is_primary;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
      })
      .addCase(authenticate.rejected, state => {
        state.checked = true;
      })
      .addCase(verify2fa.fulfilled, (state, { payload }) => {
        if (!payload?.verified) return;

        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.admin_role = payload.admin_role;
        state.is_primary = payload.is_primary;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        if (payload.error || payload.result?.two_factor_auth_enabled) return;

        state.company_id = payload.result.company_id;
        state.user_id = payload.result.user_id;
        state.admin = payload.result.admin;
        state.admin_role = payload.result.admin_role;
        state.is_primary = payload.result.is_primary;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
      })
      .addCase(loginAs.fulfilled, (state, { payload }) => {
        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.admin_role = payload.admin_role;
        state.is_primary = payload.is_primary;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
      })
      .addCase(loginWithGoogle.fulfilled, (state, { payload }) => {
        if (payload.error) return;

        state.company_id = payload.result.company_id;
        state.user_id = payload.result.user_id;
        state.admin = payload.result.admin;
        state.admin_role = payload.result.admin_role;
        state.is_primary = payload.result.is_primary;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
      })
      .addCase(verifySSO.fulfilled, (state, { payload }) => {
        state.company_id = payload.company_id;
        state.user_id = payload.user_id;
        state.admin = payload.admin;
        state.admin_role = payload.admin_role;
        state.two_factor_auth_enabled = payload.two_factor_auth_enabled;
        state.enforce_two_factor_auth = payload.enforce_two_factor_auth;
        state.checked = true;
        state.logged = true;
      })
      .addCase(enable2FA.fulfilled, state => {
        state.two_factor_auth_enabled = true;
      });
  },
});

export const { logout, stopInterpretation } = authenticationSlice.actions;

export const selectAuthentication = state => state.authentication;

export default authenticationSlice.reducer;
